import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { getCatalog, getMyBilling, startTrial } from "@/services/billing";
import { ApiError } from "@/lib/api/error-handler";
import { useAuth } from "@/lib/auth/auth-context";
import { setFlag } from "@/lib/onboarding/journeyGuard";
import type { IPlan } from "@/types/billing";

export type PricingState =
  | "skeleton"
  | "guest"
  | "trial-available"
  | "trial-active"
  | "trial-used"
  | "active-plan"
  | "expired-plan"
  | "selecting"
  | "success"
  | "error-load"
  | "error-401"
  | "error-422"
  | "error-500"
  | "empty";

type Ctx = {
  states: Set<PricingState>;
  isOn: (s: PricingState) => boolean;
  toggle: (s: PricingState) => void;
  clear: () => void;
  plans: IPlan[];
  reload: () => void;
  trialBusy: boolean;
  activateTrial: () => void;
  loggedIn: boolean;
  trialUsed: boolean;
  trialActive: boolean;
  trialDaysLeft: number;
  activePlanId: string | null;
  expired: boolean;
  selectingPlanId: string | null;
  successMessage: string | null;
  errorMessage: string | null;
  loading: boolean;
};

const PricingStateContext = createContext<Ctx | null>(null);

const EXCLUSIVE_GROUPS: PricingState[][] = [
  ["guest", "trial-available", "trial-active", "trial-used", "active-plan", "expired-plan"],
  ["error-401", "error-422", "error-500"],
  ["selecting", "success"],
];

function daysUntil(iso: string): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
}

export function PricingStateProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const router = useRouter();
  const [states, setStates] = useState<Set<PricingState>>(new Set(["skeleton"]));
  const [trialBusy, setTrialBusy] = useState(false);
  const [plans, setPlans] = useState<IPlan[]>([]);
  const [activePlanKey, setActivePlanKey] = useState<string | null>(null);
  const [realTrialDays, setRealTrialDays] = useState<number | null>(null);
  const [tick, setTick] = useState(0);

  const isOn = (s: PricingState) => states.has(s);

  const toggle = (s: PricingState) =>
    setStates((prev) => {
      const next = new Set(prev);
      if (next.has(s)) {
        next.delete(s);
      } else {
        for (const group of EXCLUSIVE_GROUPS) {
          if (group.includes(s)) {
            for (const g of group) next.delete(g);
          }
        }
        next.add(s);
      }
      return next;
    });

  const clear = () => setStates(new Set(["guest"]));

  const reload = useCallback(() => setTick((t) => t + 1), []);

  const activateTrial = useCallback(async () => {
    if (trialBusy) return;
    setTrialBusy(true);
    try {
      await startTrial();
      setFlag("planSelected", true);
      setFlag("paymentDone", true);
      reload();
      toast.success("دوره آزمایشی رایگان فعال شد");
      router.push("/onboarding/business");
    } catch (error) {
      if (!(error instanceof ApiError)) {
        toast.error("فعال‌سازی دوره آزمایشی انجام نشد");
      }
    } finally {
      setTrialBusy(false);
    }
  }, [reload, router, trialBusy]);

  useEffect(() => {
    if (status === "loading") return;
    let active = true;
    setStates(new Set(["skeleton"]));
    setPlans([]);
    Promise.all([getCatalog(), status === "authenticated" ? getMyBilling() : Promise.resolve(null)])
      .then(([catalog, summary]) => {
        if (!active) return;
        const visible = catalog.plans.filter((p) => !p.archived_at);
        setPlans(visible);
        if (visible.length === 0) {
          setStates(new Set(["empty"]));
          return;
        }
        if (summary === null) {
          setStates(new Set(["guest"]));
          return;
        }
        setActivePlanKey(summary.plan?.key ?? null);
        const expires = summary.subscription?.expires_at;
        if (expires) setRealTrialDays(daysUntil(expires));
        if (summary.subscription?.status === "expired") {
          setStates(new Set(["expired-plan"]));
          return;
        }
        if (summary.plan?.is_trial) {
          setStates(new Set(["trial-active"]));
          return;
        }
        if (summary.plan !== null) {
          setStates(new Set(["active-plan"]));
          return;
        }
        setStates(new Set(["trial-available"]));
      })
      .catch(() => {
        if (active) setStates(new Set(["error-load"]));
      });
    return () => {
      active = false;
    };
  }, [status, tick]);

  const value = useMemo<Ctx>(() => {
    const has = (s: PricingState) => states.has(s);
    const loggedIn =
      has("trial-available") ||
      has("trial-active") ||
      has("trial-used") ||
      has("active-plan") ||
      has("expired-plan");
    return {
      states,
      isOn,
      toggle,
      clear,
      plans,
      reload,
      trialBusy,
      activateTrial,
      loggedIn,
      trialUsed: has("trial-used") || has("active-plan") || has("expired-plan"),
      trialActive: has("trial-active"),
      trialDaysLeft: has("trial-active") ? (realTrialDays ?? 9) : 14,
      activePlanId:
        has("trial-active") || has("active-plan") || has("expired-plan") ? activePlanKey : null,
      expired: has("expired-plan"),
      selectingPlanId: has("selecting") ? "growth" : null,
      successMessage: has("success") ? "پلن جهش با موفقیت فعال شد. در حال انتقال به فاکتور…" : null,
      errorMessage: has("error-401")
        ? "برای ادامه ابتدا وارد حساب خود شوید."
        : has("error-422")
          ? "این پلن در دسترس نیست."
          : has("error-500")
            ? "خطا رخ داد. لطفاً دوباره تلاش کنید."
            : has("error-load")
              ? "بارگذاری پلن‌ها با خطا مواجه شد. اتصال خود را بررسی کنید."
              : null,
      loading: has("skeleton"),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activateTrial, states, plans, activePlanKey, realTrialDays, trialBusy]);

  return <PricingStateContext.Provider value={value}>{children}</PricingStateContext.Provider>;
}

export function usePricingState() {
  const ctx = useContext(PricingStateContext);
  if (!ctx) throw new Error("usePricingState must be used within PricingStateProvider");
  return ctx;
}
